// src/components/Home.tsx
import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import {
    Container,
    Typography,
    Button,
    Box,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
} from '@mui/material';

import chatAgentScreenshot from '../assets/chat_agent_screenshot.png';
import stockAnalystScreenshot from '../assets/stock_analyst_screenshot.png';
import chatAgentFrame from '../assets/chat_agent_frame.jpg';
import stockAnalystFrame from '../assets/stock_analyst_frame.jpg';
import aiFinancialAnalystIllustration from '../assets/ai_financial_analyst_illustration.jpg';

import alphaVantageLogo from '../assets/alpha_vantage_logo.png';
import tavilyLogo from '../assets/tavily_logo.png';
import openAILogo from '../assets/openai_logo.png';

const Home: React.FC = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/chat', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(to bottom right, #0f2027, #203a43, #2c5364)',
                    py: { xs: 10, md: 14 },
                    color: '#f5f5f5',
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        {/* Text Section */}
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="h2"
                                component="h1"
                                gutterBottom
                                sx={{
                                    fontWeight: 'bold',
                                    color: '#00e5ff',
                                    textShadow: '0 2px 10px rgba(0,229,255,0.7)',
                                }}
                            >
                                Shmoney AI
                            </Typography>
                            <Typography variant="h5" gutterBottom sx={{ color: '#e0f7fa', fontWeight: 300 }}>
                                AI Financial Analyst
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ color: '#cfd8dc', fontSize: '1.1rem' }}>
                                Leverage our financial AI agents that integrate artificial intelligence with real-time market data.                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/signup')}
                                sx={{
                                    mt: 3,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    background: 'linear-gradient(to right, #00e5ff, #00bfa5)',
                                    boxShadow: '0 4px 20px rgba(0,229,255,0.5)',
                                    '&:hover': {
                                        background: 'linear-gradient(to right, #00bfa5, #00e5ff)',
                                    }
                                }}
                            >
                                Get Started
                            </Button>
                        </Grid>

                        {/* Illustration Section */}
                        <Grid item xs={12} md={6}>
                            <Box
                                component="img"
                                src={aiFinancialAnalystIllustration}
                                alt="AI Financial Analyst Illustration"
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: 2,
                                    boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                                    transition: 'transform 0.5s ease',
                                    '&:hover': {
                                        transform: 'scale(1.05)'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Container maxWidth="lg" sx={{ mt: 10 }}>
                {/* Financial Analyst Section */}
                <Box>
                    <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#1e88e5' }}>
                        Financial Analyst Agent
                    </Typography>
                    <Typography variant="body1" align="center" sx={{ mb: 4, color: '#616161' }}>
                        Uncover insights with detailed reports and clear, numeric ratings.
                    </Typography>
                    <Grid container spacing={4} justifyContent="center">
                        <Grid item xs={12} md={10}>
                            <Card sx={{ p: 2, borderRadius: '8px', boxShadow: 3 }}>
                                <CardMedia
                                    component="img"
                                    image={stockAnalystFrame}
                                    alt="Stock Analysis Frame"
                                    sx={{
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: 2,
                                        mb: 2,
                                    }}
                                />
                                <CardContent>
                                    <Typography gutterBottom variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
                                        Financial Analyst Tool
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 2 }}>
                                        In-depth reports on your chosen stocks, rated on a scale of 0 to 80.
                                    </Typography>
                                    <Box mt={2}>
                                        <Box
                                            component="img"
                                            src={stockAnalystScreenshot}
                                            alt="Stock Analyst Screenshot"
                                            sx={{ width: '100%', borderRadius: 2, boxShadow: 1 }}
                                        />
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                                    <Button
                                        size="large"
                                        variant="contained"
                                        onClick={() => navigate('/signup')}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 'bold',
                                            background: 'linear-gradient(to right, #1e88e5, #1565c0)',
                                            color: '#fff',
                                            boxShadow: '0 4px 15px rgba(30,136,229,0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(to right, #1565c0, #1e88e5)',
                                            }
                                        }}
                                    >
                                        Try Now
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Financial Chat Agent Section */}
                <Box mt={10}>
                    <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#1e88e5' }}>
                        Financial Chat Agent
                    </Typography>
                    <Typography variant="body1" align="center" sx={{ mb: 4, color: '#616161' }}>
                        Chat with an AI advisor backed by the latest financial data.
                    </Typography>
                    <Grid container spacing={4} justifyContent="center">
                        <Grid item xs={12} md={10}>
                            <Card sx={{ p: 2, borderRadius: '8px', boxShadow: 3 }}>
                                <CardMedia
                                    component="img"
                                    image={chatAgentFrame}
                                    alt="Chat Agent Frame"
                                    sx={{
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: 2,
                                        mb: 2,
                                    }}
                                />
                                <CardContent>
                                    <Typography gutterBottom variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
                                        Financial Chat Agent
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 2 }}>
                                        Engage in conversation for timely insights, market trends, and forecasts.
                                    </Typography>
                                    <Box mt={2}>
                                        <Box
                                            component="img"
                                            src={chatAgentScreenshot}
                                            alt="Chat Agent Screenshot"
                                            sx={{ width: '100%', borderRadius: 2, boxShadow: 1 }}
                                        />
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                                    <Button
                                        size="large"
                                        variant="contained"
                                        onClick={() => navigate('/signup')}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 'bold',
                                            background: 'linear-gradient(to right, #1e88e5, #1565c0)',
                                            color: '#fff',
                                            boxShadow: '0 4px 15px rgba(30,136,229,0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(to right, #1565c0, #1e88e5)',
                                            }
                                        }}
                                    >
                                        Chat Now
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Technologies Section */}
                <Box mt={10}>
                    <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#1e88e5' }}>
                        Our Technology Stack
                    </Typography>
                    <Typography variant="body1" align="center" sx={{ mb: 4, color: '#616161' }}>
                        Empowered by best-in-class APIs and LLMs.
                    </Typography>
                    <Grid container spacing={4} justifyContent="center">
                        <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                            <a
                                href="https://www.alphavantage.co"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img src={alphaVantageLogo} alt="Alpha Vantage" height="50" />
                            </a>
                            <Typography variant="body1" sx={{ mt: 1 }}>
                                Financial Data
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                            <a href="https://tavily.com" target="_blank" rel="noopener noreferrer">
                                <img src={tavilyLogo} alt="Tavily" height="50" />
                            </a>
                            <Typography variant="body1" sx={{ mt: 1 }}>
                                Intelligent Web Search
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                            <a href="https://openai.com" target="_blank" rel="noopener noreferrer">
                                <img src={openAILogo} alt="OpenAI" height="50" />
                            </a>
                            <Typography variant="body1" sx={{ mt: 1 }}>
                                Cutting-Edge LLMs
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Pricing Section */}
                <Box mt={10} mb={10}>
                    <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#1e88e5' }}>
                        Pricing
                    </Typography>
                    <Typography variant="body1" align="center" sx={{ mb: 4, color: '#616161' }}>
                        Plans tailored to your needs.
                    </Typography>
                    <Grid container spacing={4} justifyContent="center">
                        {/* Free Version */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: '8px', boxShadow: 3 }}>
                                <CardContent>
                                    <Typography variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
                                        Free
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                                        Integrate your own Tavily, OpenAI, and Alpha Vantage Premium keys.
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="large"
                                        fullWidth
                                        onClick={() => navigate('/signup')}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 'bold',
                                            background: 'linear-gradient(to right, #1e88e5, #1565c0)',
                                            color: '#fff',
                                            boxShadow: '0 4px 15px rgba(30,136,229,0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(to right, #1565c0, #1e88e5)',
                                            }
                                        }}
                                    >
                                        Sign Up
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                        {/* One-off Top-up */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: '8px', boxShadow: 3 }}>
                                <CardContent>
                                    <Typography variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
                                        One-Off Top-Up
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                                        $10 for 500 Chat messages + 125 Analyst reports.
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="large"
                                        fullWidth
                                        onClick={() => navigate('/signup')}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 'bold',
                                            background: 'linear-gradient(to right, #1e88e5, #1565c0)',
                                            color: '#fff',
                                            boxShadow: '0 4px 15px rgba(30,136,229,0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(to right, #1565c0, #1e88e5)',
                                            }
                                        }}
                                    >
                                        Buy Now
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                        {/* Monthly Subscription */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: '8px', boxShadow: 3 }}>
                                <CardContent>
                                    <Typography variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
                                        Monthly Plan
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                                        $35/month for 2000 Chat messages + 500 Analyst reports.
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="large"
                                        fullWidth
                                        onClick={() => navigate('/signup')}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 'bold',
                                            background: 'linear-gradient(to right, #1e88e5, #1565c0)',
                                            color: '#fff',
                                            boxShadow: '0 4px 15px rgba(30,136,229,0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(to right, #1565c0, #1e88e5)',
                                            }
                                        }}
                                    >
                                        Subscribe
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </Container>

            {/* Footer */}
            <Box
                sx={{
                    backgroundColor: '#212121',
                    color: '#fff',
                    py: 3,
                    textAlign: 'center',
                }}
            >
                <Typography variant="body2">
                    &copy; {new Date().getFullYear()} Shmoney AI. All rights reserved.
                </Typography>
            </Box>
        </>
    );
};

export default Home;
